import * as queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { MapDispatchToProps } from '~commonInfra/utils/ConnectUtils';
import { UrlParamKeys } from '~src/commonInfra/url/UrlParamKeys';
import { enterSpace } from '~src/spacesPlatform/actions/EnterSpaceAction';
import { setContentFilterNoRequestContent } from '~src/spacesPlatform/actions/filters/FilterActions';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { setOrderAction } from '~src/spacesPlatform/actions/SetOrderAction';
import { getOrderClausesFromQueryParam } from '~src/spacesPlatform/components/Explorer/ExplorerUrlBindings';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ContentFilterKeyName,ContentFiltersKeys } from '~src/spacesPlatform/models/ContentFilters';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '~src/spacesPlatform/models/sort/SortOptionDefinitions';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { AllSiteUsersSubspaceName, SpaceInfo, SpaceItemForNonEntitySpace } from '~src/spacesPlatform/state/SpaceInfo';

import { ConnectedQueryParamUpdateProvider } from '../../../../bootstrap/routerHelpers/QueryParamUpdateProvider';
import { AllSiteUsersAndIdentitiesSpaceOwnProps } from './AllSiteUsersAndIdentitiesSpace';
import { ConnectedAllSiteUsersAndIdentitiesSpace } from './ConnectedAllSiteUsersAndIdentitiesSpace';

export interface AllSiteUsersAndIdentitiesSpaceProviderContainerOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly subspaceName: AllSiteUsersSubspaceName;

  readonly siteName: string;

  readonly renderSpace: (props: AllSiteUsersAndIdentitiesSpaceOwnProps) => JSX.Element;
  readonly routeLocationSearch: string;
}

export interface AllSiteUsersAndIdentitiesSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
  readonly setContentFilterNoRequestContent: (filterType: ContentFilterKeyName, value: string) => void;
}

export type AllSiteUsersAndIdentitiesSpaceProviderContainerProps = AllSiteUsersAndIdentitiesSpaceProviderContainerOwnProps & AllSiteUsersAndIdentitiesSpaceProviderDispatchProps;

export class AllSiteUsersAndIdentitiesSpaceProviderContainer extends React.PureComponent<AllSiteUsersAndIdentitiesSpaceProviderContainerProps> {
  public render() {
    return this.props.renderSpace(this.props);
  }

  public componentDidMount() {
    this.props.requestSpaceContents();
  }

  public componentDidUpdate(prevProps: AllSiteUsersAndIdentitiesSpaceProviderContainerProps) {
    // This is a workaround because ConnectedSpaceInfoProvider does not have logic for enter space on component update.
    if (this.props.subspaceName !== prevProps.subspaceName) {
      this.changeSubspace();
    }
  }

  private changeSubspace() {
    let spaceInfo: SpaceInfo = {
      item: SpaceItemForNonEntitySpace,
      spaceType: 'siteusers',
      subspaceName: this.props.subspaceName,
      // Identities subspace does not support infinite scroll feature.
      isKeysetPaginationSpace: FeatureFlags.UserGridInfiniteScrollEnabled && this.props.subspaceName === 'list'
    };
    this.props.enterSpace(spaceInfo);

    this.clearAndSetSortOrderFromURL(spaceInfo);
    this.setFilterTypesFromUrl();
    this.props.requestSpaceContents();
  }

  private clearAndSetSortOrderFromURL(spaceInfo: SpaceInfo) {
    const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(['siteuser'], spaceInfo);
    const values = queryString.parse(this.props.routeLocationSearch);

    const orderToSet = getOrderClausesFromQueryParam(values[UrlParamKeys.Order], defaultOrder);
    this.props.setOrderAction(orderToSet);
  }

  private setFilterTypesFromUrl() {
    const currentQueryValues = queryString.parse(this.props.routeLocationSearch);
    for (const filterType of _.keys(ContentFiltersKeys)) {
      const value = currentQueryValues[filterType];
      this.props.setContentFilterNoRequestContent(filterType as ContentFilterKeyName, value ? value + '' : undefined);
    }
  }
}

const mapDispatchToProps: MapDispatchToProps<AllSiteUsersAndIdentitiesSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  enterSpace,
  requestSpaceContents,
  setOrderAction,
  setContentFilterNoRequestContent
}, dispatch);

export const ConnectedAllSiteUsersAndIdentitiesSpaceProviderContainer: React.ComponentClass<AllSiteUsersAndIdentitiesSpaceProviderContainerOwnProps>
  = connect(undefined, mapDispatchToProps)(AllSiteUsersAndIdentitiesSpaceProviderContainer);

const renderSpace = (props: AllSiteUsersAndIdentitiesSpaceOwnProps): JSX.Element => <ConnectedAllSiteUsersAndIdentitiesSpace {...props} />;

export interface AllSiteUsersAndIdentitiesSpaceProviderOwnProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}
export type AllSiteUsersAndIdentitiesSpaceProviderProps = AllSiteUsersAndIdentitiesSpaceProviderOwnProps & Omit<AllSiteUsersAndIdentitiesSpaceProviderContainerOwnProps, 'renderSpace'>;

export const AllSiteUsersAndIdentitiesSpaceProvider: React.FunctionComponent<AllSiteUsersAndIdentitiesSpaceProviderProps> = (props) => {

  const spaceInfo: SpaceInfo = {
    item: SpaceItemForNonEntitySpace,
    spaceType: 'siteusers',
    subspaceName: props.subspaceName,
    // Identities subspace does not support infinite scroll feature.
    isKeysetPaginationSpace: FeatureFlags.UserGridInfiniteScrollEnabled && props.subspaceName === 'list'
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedQueryParamUpdateProvider
        routeLocationPath={props.routeLocationPath}
        routeLocationSearch={props.routeLocationSearch}
        shouldUpdateBreadcrumbs={true}
        shouldUpdateContentTypes={false}
        shouldUpdateFilters={true}
        shouldUpdateSort={true}
      >
        <ConnectedAllSiteUsersAndIdentitiesSpaceProviderContainer {...props} renderSpace={renderSpace}/>
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
};
